import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Avatar } from '../../Avatar';
import { usePatientMetadata } from '../../../providers/PatientMetadataProvider';
import { StartMonitoringPatient } from '../../../services/patient';
import { StartMonitoringParams } from '../../../services/patient/delivery';
import { fetchQuickstarts, DmQuickstartView } from '../../../services/patient/quickstarts';

const MIN_ALIGNER = 1;
const MAX_ALIGNER = 200;

// Radio-card accent: reuses the design system's established "Start
// monitoring" teal (see conventions.md) rather than introducing a foreign
// blue accent for the selected state.
const PHASE_ACCENT_BORDER = 'border-[#007E87]';
const PHASE_ACCENT_BG = 'bg-[#007E87]/5';

interface StartMonitoringFormProps {
    patient: StartMonitoringPatient | null;
    onConfirm: (params: StartMonitoringParams) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
    showAlignerNumbers?: boolean;
}

type TreatmentPhase = 'initial' | 'refinement';

// Paused monitoring is resumed with its existing quickstart, so the phase
// selection (and the quickstart fetch) is skipped entirely.
function isMonitoringPaused(patient: StartMonitoringPatient | null): boolean {
    return (patient?.monitoring?.monitoringStatus ?? '').toLowerCase() === 'paused';
}

function normalizeText(text: string): string {
    return text.toLowerCase().trim().replace(/\s+/g, ' ');
}

function findMatchingQuickstart(
    quickstarts: DmQuickstartView[],
    phase: TreatmentPhase
): DmQuickstartView | undefined {
    const keyword = phase === 'initial' ? 'initial case' : 'refinement';
    return quickstarts.find((q) => normalizeText(q.name).includes(keyword));
}

/**
 * Reusable body of the start/resume monitoring flow: patient info card,
 * treatment-phase selection (fuzzy-matched to DM quickstarts), aligner
 * number inputs, and the paused-resume variant. No modal chrome (overlay,
 * title, close button) — callers own that. Used by `StartMonitoringModal`
 * (workflow entry point, full modal) and `ManageMonitoringModal`'s 'start'
 * step (start-only, aligner numbers hidden, no paused variant).
 */
function StartMonitoringForm({
    patient,
    onConfirm,
    onCancel,
    isSubmitting = false,
    showAlignerNumbers = true
}: StartMonitoringFormProps) {
    const [selectedPhase, setSelectedPhase] = useState<TreatmentPhase | null>(null);
    const [currentAligner, setCurrentAligner] = useState<number | ''>('');
    const [totalAligner, setTotalAligner] = useState<number | ''>('');
    const { getProfilePictureUrl, getProfileThumbnailUrl } = usePatientMetadata();

    const patientId = patient?.id ?? null;
    const profilePictureUrl = getProfilePictureUrl(patientId ?? undefined);
    const profileThumbnailUrl = getProfileThumbnailUrl(patientId ?? undefined);

    const isPaused = isMonitoringPaused(patient);

    const {
        data: quickstarts = [],
        isLoading: isLoadingQuickstarts,
        isError: isQuickstartsError
    } = useQuery({
        queryKey: ['quickstarts', patientId],
        queryFn: () => fetchQuickstarts(patientId!),
        enabled: patientId !== null && !isPaused,
        staleTime: 5 * 60 * 1000
    });

    const initialMatch = findMatchingQuickstart(quickstarts, 'initial');
    const refinementMatch = findMatchingQuickstart(quickstarts, 'refinement');

    const isInitialAvailable = !isLoadingQuickstarts && initialMatch !== undefined;
    const isRefinementAvailable = !isLoadingQuickstarts && refinementMatch !== undefined;

    const handlePhaseSelect = (phase: TreatmentPhase, isAvailable: boolean) => {
        if (!isAvailable || isSubmitting) return;
        setSelectedPhase(phase);
    };

    const isCurrentAlignerValid =
        currentAligner !== '' &&
        Number.isInteger(currentAligner) &&
        currentAligner >= MIN_ALIGNER &&
        currentAligner <= MAX_ALIGNER;

    const isTotalAlignerValid =
        totalAligner !== '' &&
        Number.isInteger(totalAligner) &&
        totalAligner >= MIN_ALIGNER &&
        totalAligner <= MAX_ALIGNER;

    const areAlignersConsistent =
        isCurrentAlignerValid &&
        isTotalAlignerValid &&
        (totalAligner as number) >= (currentAligner as number);

    const handleConfirm = () => {
        if (showAlignerNumbers && !areAlignersConsistent) return;

        if (isPaused) {
            onConfirm({
                currentAligner: showAlignerNumbers ? (currentAligner as number) : 0,
                totalAligner: showAlignerNumbers ? (totalAligner as number) : 0
            });
            return;
        }

        if (!selectedPhase) return;

        const match = selectedPhase === 'initial' ? initialMatch : refinementMatch;
        if (!match) return;

        onConfirm({
            quickstart: match.id,
            quickstartName: match.name,
            currentAligner: showAlignerNumbers ? (currentAligner as number) : 0,
            totalAligner: showAlignerNumbers ? (totalAligner as number) : 0
        });
    };

    const selectedMatch =
        selectedPhase === 'initial'
            ? initialMatch
            : selectedPhase === 'refinement'
              ? refinementMatch
              : undefined;
    const isConfirmEnabled =
        (isPaused || (selectedPhase !== null && selectedMatch !== undefined)) &&
        (showAlignerNumbers ? areAlignersConsistent : true) &&
        !isSubmitting;

    const phaseButtonClass = (phase: TreatmentPhase, isAvailable: boolean): string => {
        if (!isAvailable) {
            return 'w-full text-left px-4 py-3 rounded-lg border-2 border-gray-100 bg-gray-50 cursor-not-allowed opacity-50';
        }
        if (selectedPhase === phase) {
            return `w-full text-left px-4 py-3 rounded-lg border-2 ${PHASE_ACCENT_BORDER} ${PHASE_ACCENT_BG} transition-colors`;
        }
        return 'w-full text-left px-4 py-3 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors';
    };

    const phaseTextClass = (isAvailable: boolean): string =>
        isAvailable ? 'font-semibold text-gray-800' : 'font-semibold text-gray-400';

    const radioDotClass = (isSelected: boolean): string =>
        `w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
            isSelected ? `${PHASE_ACCENT_BORDER} bg-[#007E87]` : 'border-gray-300'
        }`;

    return (
        <>
            {/* Content */}
            <div className="px-6 py-6 space-y-5">
                {/* Patient Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 flex-shrink-0">
                            <Avatar
                                user={{
                                    name: patient?.name || 'N/A',
                                    email: patient?.email || ''
                                }}
                                imageURL={profilePictureUrl || ''}
                                thumbnailURL={profileThumbnailUrl ?? undefined}
                            />
                        </div>
                        <div className="flex-1">
                            <div className="text-sm font-semibold text-gray-600 mb-1">Patient</div>
                            <div className="text-lg font-bold text-gray-800">
                                {patient?.name || 'N/A'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Paused: resumed with the existing quickstart, no phase to pick */}
                {isPaused && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-600">
                        Monitoring is paused for this patient. Confirming will resume it with its
                        existing quickstart. Just check the aligner numbers below.
                    </div>
                )}

                {/* Treatment Phase Selection */}
                {!isPaused && (
                    <div className="space-y-3">
                        <div className="text-sm font-semibold text-gray-700 mb-2">
                            Select treatment phase
                        </div>

                        {isQuickstartsError && (
                            <p className="text-sm text-red-500">
                                Failed to load available treatment phases. Please close and try
                                again.
                            </p>
                        )}

                        {/* Initial Treatment */}
                        <button
                            onClick={() => handlePhaseSelect('initial', isInitialAvailable)}
                            disabled={!isInitialAvailable || isSubmitting}
                            className={phaseButtonClass('initial', isInitialAvailable)}
                        >
                            <div className="flex items-center gap-3">
                                <span className={radioDotClass(selectedPhase === 'initial')}>
                                    {selectedPhase === 'initial' && (
                                        <span className="w-1.5 h-1.5 rounded-full bg-white" />
                                    )}
                                </span>
                                <div className="flex-1">
                                    <div className={phaseTextClass(isInitialAvailable)}>
                                        My patient is starting treatment
                                    </div>
                                </div>
                            </div>
                        </button>

                        {/* Refinement */}
                        <button
                            onClick={() => handlePhaseSelect('refinement', isRefinementAvailable)}
                            disabled={!isRefinementAvailable || isSubmitting}
                            className={phaseButtonClass('refinement', isRefinementAvailable)}
                        >
                            <div className="flex items-center gap-3">
                                <span className={radioDotClass(selectedPhase === 'refinement')}>
                                    {selectedPhase === 'refinement' && (
                                        <span className="w-1.5 h-1.5 rounded-full bg-white" />
                                    )}
                                </span>
                                <div className="flex-1">
                                    <div className={phaseTextClass(isRefinementAvailable)}>
                                        My patient is starting a refinement
                                    </div>
                                </div>
                            </div>
                        </button>
                    </div>
                )}
                {/* Aligner Numbers */}
                {showAlignerNumbers && (
                    <div className="space-y-3">
                        <div className="text-sm font-semibold text-gray-700">Aligner numbers</div>
                        <div className="flex gap-4">
                            <div className="flex-1 space-y-1">
                                <label
                                    className="text-xs font-semibold text-gray-700"
                                    htmlFor="currentAligner"
                                >
                                    Current aligner
                                </label>
                                <input
                                    id="currentAligner"
                                    type="number"
                                    min={MIN_ALIGNER}
                                    max={MAX_ALIGNER}
                                    step={1}
                                    value={currentAligner}
                                    disabled={isSubmitting}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        // Digits only — reject decimals/non-numeric so "1.5" can't
                                        // silently become 1.
                                        if (raw !== '' && !/^\d+$/.test(raw)) return;
                                        setCurrentAligner(raw === '' ? '' : parseInt(raw, 10));
                                    }}
                                    placeholder="e.g. 1"
                                    className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 ${
                                        currentAligner !== '' && !isCurrentAlignerValid
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            : 'border-gray-300'
                                    }`}
                                />
                                {currentAligner !== '' && !isCurrentAlignerValid && (
                                    <p className="text-xs text-red-500">
                                        Must be a whole number between {MIN_ALIGNER} and{' '}
                                        {MAX_ALIGNER}.
                                    </p>
                                )}
                            </div>
                            <div className="flex-1 space-y-1">
                                <label
                                    className="text-xs font-semibold text-gray-700"
                                    htmlFor="totalAligner"
                                >
                                    Total aligners
                                </label>
                                <input
                                    id="totalAligner"
                                    type="number"
                                    min={MIN_ALIGNER}
                                    max={MAX_ALIGNER}
                                    step={1}
                                    value={totalAligner}
                                    disabled={isSubmitting}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        // Digits only — reject decimals/non-numeric so "1.5" can't
                                        // silently become 1.
                                        if (raw !== '' && !/^\d+$/.test(raw)) return;
                                        setTotalAligner(raw === '' ? '' : parseInt(raw, 10));
                                    }}
                                    placeholder="e.g. 20"
                                    className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 ${
                                        totalAligner !== '' && !isTotalAlignerValid
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            : 'border-gray-300'
                                    }`}
                                />
                                {totalAligner !== '' && !isTotalAlignerValid && (
                                    <p className="text-xs text-red-500">
                                        Must be a whole number between {MIN_ALIGNER} and{' '}
                                        {MAX_ALIGNER}.
                                    </p>
                                )}
                                {isCurrentAlignerValid &&
                                    isTotalAlignerValid &&
                                    (totalAligner as number) < (currentAligner as number) && (
                                        <p className="text-xs text-red-500">
                                            Total must be &ge; current aligner.
                                        </p>
                                    )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                <button
                    onClick={onCancel}
                    disabled={isSubmitting}
                    className={`px-4 py-2 text-sm font-semibold rounded-lg border ${
                        isSubmitting
                            ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
                            : 'text-gray-800 bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                >
                    Cancel
                </button>
                <button
                    onClick={handleConfirm}
                    disabled={!isConfirmEnabled}
                    className={`px-4 py-2 text-sm font-bold rounded-lg flex items-center gap-2 ${
                        isConfirmEnabled
                            ? 'bg-gray-800 text-white hover:bg-gray-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                >
                    {isSubmitting && (
                        <svg
                            className="animate-spin h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                        </svg>
                    )}
                    {isSubmitting
                        ? isPaused
                            ? 'Resuming...'
                            : 'Starting...'
                        : isPaused
                          ? 'Resume Monitoring'
                          : 'Start Monitoring'}
                </button>
            </div>
        </>
    );
}

export { StartMonitoringForm };
